import React from 'react';
import { useQuery } from '@apollo/client';
import { IconButton, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import getClosingDataPayrollPeriod from '../../../../../Apollo/queries/getClosingDataPayrollPeriod';

function lastPaylaodMonth(dateOfLastPayload: string) {
  if (!dateOfLastPayload) {
    const startDate = '2011-03-05T14:48:00.000Z'.split('-');
    const splitStartDateT = startDate[2].split('T');
    const newDate = new Date(parseInt(startDate[0], 10), parseInt(startDate[1], 10), parseInt(splitStartDateT[0], 10));
    const lastPayloadMonth = newDate.getMonth() - 1;
    const actualDate = new Date();
    let minDate = null;
    if (actualDate.getMonth() > lastPayloadMonth) {
      minDate = new Date(actualDate.getFullYear(), actualDate.getMonth() - 1, 1);
    } else {
      minDate = new Date(actualDate.getFullYear(), actualDate.getMonth(), 1);
    }
    return minDate;
  }
  const startDate = dateOfLastPayload.split('-');
  const splitStartDateT = startDate[2].split('T');
  const newDate = new Date(parseInt(startDate[0], 10), parseInt(startDate[1], 10), parseInt(splitStartDateT[0], 10));
  const lastPayloadMonth = newDate.getMonth() - 1;
  const actualDate = new Date();
  let minDate = null;
  if (actualDate.getMonth() > lastPayloadMonth) {
    minDate = new Date(actualDate.getFullYear(), actualDate.getMonth() - 1, 1);
  } else {
    minDate = new Date(actualDate.getFullYear(), actualDate.getMonth(), 1);
  }
  return minDate;
}

interface Props {
  value: string | undefined;
  onValueChange: (value: string | null) => void;
  readOnly?: boolean;
  lockTooltip?: string;
  allowClear?: boolean;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Zeitzonenfeste Monatsserialisierung über lokale Kalenderfelder (kein toISOString/UTC). */
function formatYearMonthLocal(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

function DateEditor({ value, onValueChange, readOnly = false, lockTooltip, allowClear = false }: Props): JSX.Element {
  const { data: dateOfLastPayload } = useQuery(getClosingDataPayrollPeriod);
  const hasClearableValue = value != null && value !== '';
  const showClear = allowClear && hasClearableValue && !readOnly;

  const picker = (
    <DesktopDatePicker
      views={['year', 'month']}
      // eslint-disable-next-line react/jsx-props-no-spreading
      renderInput={(params) => <TextField {...params} disabled={readOnly || params.disabled} />}
      minDate={lastPaylaodMonth(dateOfLastPayload?.getClosingDataPayrollPeriod)}
      value={value || ''}
      disabled={readOnly}
      readOnly={readOnly}
      onChange={(date) => {
        if (readOnly) {
          return null;
        }
        if (date) {
          return onValueChange(formatYearMonthLocal(date));
        }
        if (allowClear) {
          return onValueChange(null);
        }
        return null;
      }}
    />
  );

  const editor = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {picker}
      {showClear ? (
        <Tooltip title="Enddatum entfernen — Leistung läuft wieder unbefristet">
          <IconButton
            size="small"
            aria-label="Enddatum entfernen"
            onClick={() => {
              onValueChange(null);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </span>
  );

  if (readOnly && lockTooltip) {
    return (
      <Tooltip title={lockTooltip}>
        <span>{picker}</span>
      </Tooltip>
    );
  }

  return editor;
}

export default DateEditor;
